import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../components/Button";
import Modal from "../../../../components/v2/Modal";
import Select, { MaterialSelectEvent } from "../../../../components/v2/Select";
import AdminTransparencyService, {
  SpreadsheetError,
  SpreadsheetReport,
  TransparencyPeriodSummary,
  spreadsheetErrors,
} from "../../../../services/AdminTransparencyService";
import { TEMPLATE_PATH, availableYears } from "./spreadsheet";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.transparency.newPeriod";
const EXTENSIONS = ["csv", "xlsx", "ods"];
const MAX_SIZE = 30 * 1024 * 1024;

type Step = "form" | "validating" | "confirm" | "creating";

interface Props {
  open: boolean;
  onClose(): void;
  onCreated(period: TransparencyPeriodSummary): void;
}

export default function NewPeriodModal({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const today = new Date();

  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<SpreadsheetError[]>([]);
  const [report, setReport] = useState<SpreadsheetReport | null>(null);
  const [step, setStep] = useState<Step>("form");
  const [dragging, setDragging] = useState(false);

  const months = t("translations.admin.transparency.months", { returnObjects: true }) as unknown as string[];
  const monthOptions = (Array.isArray(months) ? months : []).map((label, index) => ({ value: index + 1, label }));
  const yearOptions = availableYears(today).map((value) => ({ value, label: String(value) }));

  const reset = () => {
    setMonth(today.getMonth() + 1);
    setYear(today.getFullYear());
    setFile(null);
    setErrors([]);
    setReport(null);
    setStep("form");
  };

  const close = () => {
    if (step === "validating" || step === "creating") {
      return;
    }
    reset();
    onClose();
  };

  const selectFile = (selected: File | null | undefined) => {
    if (!selected) {
      return;
    }
    setFile(selected);
    setErrors(localErrors(selected));
  };

  const handleFailure = (error: unknown) => {
    setErrors(spreadsheetErrors(error) || [{ code: "generic" }]);
    setStep("form");
  };

  const create = () => {
    if (!file) {
      return;
    }
    setStep("creating");
    AdminTransparencyService.create({ month, year, file })
      .then((period) => {
        reset();
        onCreated(period);
      })
      .catch(handleFailure);
  };

  const submit = () => {
    if (!file) {
      setErrors([{ code: "file_required" }]);
      return;
    }

    const invalid = localErrors(file);
    if (invalid.length > 0) {
      setErrors(invalid);
      return;
    }

    setErrors([]);
    setStep("validating");
    AdminTransparencyService.validate({ month, year, file })
      .then((data) => {
        if (data.ignored.length > 0) {
          setReport(data);
          setStep("confirm");
        } else {
          create();
        }
      })
      .catch(handleFailure);
  };

  if (step === "validating" || step === "creating") {
    return (
      <Modal open={open} onClose={close} size="small" dismissible={false}>
        <div className={style.progress} role="status">
          <span aria-hidden="true" className={style.spinner} />
          <p className={`pgm-title-medium ${style.progressText}`}>{t(`${keyI18n}.${step}`)}</p>
        </div>
      </Modal>
    );
  }

  if (step === "confirm" && report) {
    return (
      <Modal
        open={open}
        onClose={close}
        title={t(`${keyI18n}.confirmTitle`)}
        size="large"
        actions={(
          <>
            <Button variant="text" shape="round" size="small" onClick={close}>
              {t(`${keyI18n}.cancel`)}
            </Button>
            <Button variant="filled" shape="round" size="small" onClick={create}>
              {t(`${keyI18n}.confirm`)}
            </Button>
          </>
        )}
      >
        <p className={`pgm-body-large ${style.warning}`} role="alert">
          <span aria-hidden="true" className="material-symbols-sharp">warning</span>
          {t(`${keyI18n}.ignoredWarning`)}
        </p>
        <p className="pgm-body-large">
          {t(`${keyI18n}.summary`, { count: report.attorneysCount, ignored: report.ignored.length })}
        </p>
        <ul className={style.ignoredList}>
          {report.ignored.map((row, index) => (
            <li key={`${row.registration}-${index}`} className={style.ignoredItem}>
              <span aria-hidden="true" className={`material-symbols-sharp ${style.ignoredIcon}`}>close</span>
              <span className="pgm-body-large">{row.name}</span>
              <span className={`pgm-body-medium ${style.ignoredReason}`}>{t(`${keyI18n}.reasons.${row.reason}`)}</span>
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={t(`${keyI18n}.title`)}
      closeLabel={t(`${keyI18n}.close`)}
      size="large"
      headerActions={(
        <Button
          variant="text"
          shape="round"
          size="small"
          href={TEMPLATE_PATH}
          download=""
          icon={<span className="material-symbols-sharp">download</span>}
        >
          {t("translations.admin.transparency.actions.downloadTemplate")}
        </Button>
      )}
      actions={(
        <>
          <Button variant="text" shape="round" size="small" onClick={close}>
            {t(`${keyI18n}.cancel`)}
          </Button>
          <Button variant="filled" shape="round" size="small" onClick={submit}>
            {t(`${keyI18n}.submit`)}
          </Button>
        </>
      )}
    >
      <div className={style.form}>
        <div className={style.periodFields}>
          <Select
            label={t(`${keyI18n}.month`)}
            name="month"
            variant="outlined"
            options={monthOptions}
            value={month}
            onChange={(event: MaterialSelectEvent) => setMonth(Number(event.target.value))}
          />
          <Select
            label={t(`${keyI18n}.year`)}
            name="year"
            variant="outlined"
            options={yearOptions}
            value={year}
            onChange={(event: MaterialSelectEvent) => setYear(Number(event.target.value))}
          />
        </div>

        <div className={style.fileField}>
          <span className={`pgm-label-large ${style.fileLabel}`} id="transparency-file-label">{t(`${keyI18n}.file`)}</span>
          <input
            ref={inputRef}
            id="transparency-file"
            type="file"
            accept=".csv,.xlsx,.ods"
            className={style.visuallyHidden}
            aria-labelledby="transparency-file-label"
            onChange={(event) => {
              selectFile(event.target.files && event.target.files[0]);
              event.target.value = "";
            }}
          />
          <label
            htmlFor="transparency-file"
            className={`pgm-body-large ${style.dropzone} ${dragging ? style.dropzoneActive : ""} ${errors.length > 0 ? style.dropzoneError : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              selectFile(event.dataTransfer.files[0]);
            }}
          >
            <span aria-hidden="true" className="material-symbols-sharp">{file ? "description" : "upload_file"}</span>
            <span className={style.dropzoneText}>{file ? file.name : t(`${keyI18n}.dropzone`)}</span>
            {file && <span className={`pgm-label-large ${style.dropzoneAction}`}>{t(`${keyI18n}.change`)}</span>}
          </label>
          <span className={`pgm-body-small ${style.fileHint}`}>{t(`${keyI18n}.hint`)}</span>
          {errors.length > 0 && (
            <ul className={style.fileErrors} role="alert">
              {errors.map((error, index) => (
                <li key={`${error.code}-${index}`} className="pgm-body-medium">
                  {t(`${keyI18n}.errors.${error.code}`, { ...error, defaultValue: t(`${keyI18n}.errors.generic`) })}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}

function localErrors(file: File): SpreadsheetError[] {
  const extension = (file.name.split(".").pop() || "").toLowerCase();

  if (!EXTENSIONS.includes(extension)) {
    return [{ code: "invalid_format" }];
  }
  if (file.size > MAX_SIZE) {
    return [{ code: "file_too_large" }];
  }
  return [];
}
